import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useToasts } from "../../state/toasts";
import { Empty } from "../../ui/components";
import { dateTime, money } from "../../ui/format";
import { formatQuantity } from "../../ui/units";

/**
 * Сроки годности.
 *
 * Показывает, что портится, и даёт списать просроченное одним действием.
 * Система не запрещает продажу и не списывает сама: она не знает, выкинули
 * партию или нет, а запрет остановил бы кассу в час пик из-за записи, которую
 * забыли поправить. Решает человек, экран напоминает.
 */

interface ShelfLifeRow {
  batchId: string;
  ingredientId: string;
  title: string;
  unit: string;
  quantity: string;
  unitCost: string;
  cost: string;
  receivedAt: string;
  expiresAt: string;
  daysLeft: number;
  expired: boolean;
}

interface ShelfLifeReport {
  expired: ShelfLifeRow[];
  soon: ShelfLifeRow[];
  totals: { expiredCost: string; soonCost: string };
}

/** За сколько дней вперёд смотреть. Три дня — обычный горизонт смены. */
const HORIZONS = [3, 7, 14];

export function ShelfLifePanel({ manage, onChanged }: { manage: boolean; onChanged: () => Promise<void> }) {
  const { run, notify } = useToasts();

  const [days, setDays] = useState(3);
  const [report, setReport] = useState<ShelfLifeReport | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const data = await api.get<ShelfLifeReport>(`/api/admin/stock/shelf-life?days=${days}`);
      setReport(data);
      setPicked(new Set(data.expired.map(row => row.batchId)));
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось получить сроки годности", "error");
    }
  }, [days, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!report) return <Empty>Смотрим, что портится…</Empty>;

  async function writeOff() {
    const batchIds = [...picked];
    if (batchIds.length === 0) return;

    const ok = await run(
      () => api.once.post("/api/admin/stock/shelf-life/write-off", { batchIds }),
      "Просрочка списана"
    );

    if (ok) {
      await load();
      await onChanged();
    }
  }

  function toggle(batchId: string) {
    setPicked(current => {
      const next = new Set(current);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  }

  return (
    <>
      <section className="panel">
        <header>
          <div>
            <h2>Просрочено</h2>
            <p>
              Система не запрещает продажу и не списывает сама: она не знает, выкинули партию или нет.
              Отметьте то, чего действительно нет, и спишите одним действием.
            </p>
          </div>

          {manage && report.expired.length > 0 ? (
            <button type="button" className="btn btn-primary" disabled={picked.size === 0} onClick={() => void writeOff()}>
              Списать · {picked.size}
            </button>
          ) : null}
        </header>

        {report.expired.length === 0 ? (
          <Empty>Просрочки нет.</Empty>
        ) : (
          <>
            <p className="muted">
              В мусор уйдёт <b>{money(Number(report.totals.expiredCost))}</b>.
            </p>
            <BatchTable rows={report.expired} picked={picked} onToggle={manage ? toggle : undefined} />
          </>
        )}
      </section>

      <section className="panel">
        <header>
          <div>
            <h2>Скоро испортится</h2>
            <p>Это продают в первую очередь — и списание берёт эти партии раньше остальных.</p>
          </div>

          <div className="filters">
            {HORIZONS.map(horizon => (
              <button
                key={horizon}
                type="button"
                className={`filter ${days === horizon ? "active" : ""}`}
                onClick={() => setDays(horizon)}
              >
                {horizon} дн.
              </button>
            ))}
          </div>
        </header>

        {report.soon.length === 0 ? (
          <Empty>В ближайшие {days} дн. ничего не портится.</Empty>
        ) : (
          <>
            <p className="muted">
              На <b>{money(Number(report.totals.soonCost))}</b> — успеть продать.
            </p>
            <BatchTable rows={report.soon} />
          </>
        )}
      </section>
    </>
  );
}

function BatchTable({
  rows,
  picked,
  onToggle
}: {
  rows: ShelfLifeRow[];
  picked?: Set<string>;
  onToggle?: (batchId: string) => void;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {onToggle ? <th /> : null}
            <th>Позиция</th>
            <th className="num">Остаток</th>
            <th className="num">Стоимость</th>
            <th>Пришло</th>
            <th>Годно до</th>
            <th className="num">Осталось</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.batchId} className={row.expired ? "row-alert" : ""}>
              {onToggle ? (
                <td>
                  <input
                    type="checkbox"
                    checked={picked?.has(row.batchId) ?? false}
                    onChange={() => onToggle(row.batchId)}
                  />
                </td>
              ) : null}
              <td>{row.title}</td>
              <td className="num">{formatQuantity(Number(row.quantity), row.unit)}</td>
              <td className="num">{money(Number(row.cost))}</td>
              <td className="tiny">{dateTime(row.receivedAt)}</td>
              <td className="tiny">{dateTime(row.expiresAt)}</td>
              <td className="num">
                {row.daysLeft < 0 ? (
                  <b>просрочено на {Math.abs(row.daysLeft)} дн.</b>
                ) : row.daysLeft === 0 ? (
                  <b>сегодня</b>
                ) : (
                  `${row.daysLeft} дн.`
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
